import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useState, type ReactNode } from "react";
import type { Stage, Task } from "../../api/types";
import { cx } from "../../lib/format";
import { SortableTaskCard } from "./TaskCard";

export const COLUMN_PREFIX = "col:";
/** Done tasks pile up, so the done column only shows this many until expanded. */
export const DONE_VISIBLE_LIMIT = 5;

export function BoardColumn({
  stageKey,
  stage,
  tasks,
  highlighted,
  selectedId,
  onOpen,
  header,
  footer,
  collapseAfter,
}: {
  stageKey: string;
  stage: Stage | undefined;
  tasks: Task[];
  highlighted: boolean;
  selectedId: string | undefined;
  onOpen: (t: Task) => void;
  header?: ReactNode;
  footer?: ReactNode;
  collapseAfter?: number;
}) {
  const { setNodeRef } = useDroppable({ id: COLUMN_PREFIX + stageKey, data: { type: "column", stageKey } });
  const [expanded, setExpanded] = useState(false);
  const collapsible = collapseAfter !== undefined && tasks.length > collapseAfter;
  // Only visible cards go into the SortableContext so dnd-kit never tracks unmounted nodes.
  const visible = collapsible && !expanded ? tasks.slice(0, collapseAfter) : tasks;
  const hidden = tasks.length - visible.length;
  return (
    <section className={cx("column", highlighted && "column-over")} aria-label={stage?.name ?? stageKey}>
      <header className="column-header">
        <span className="column-title">{stage?.name ?? stageKey}</span>
        {!stage && <span className="column-kind muted">not in current workflow</span>}
      </header>
      {header}
      <div ref={setNodeRef} className="column-body">
        <SortableContext items={visible.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {visible.map((t) => (
            <SortableTaskCard
              key={t.id}
              task={t}
              stage={stage}
              selected={t.id === selectedId}
              onOpen={() => onOpen(t)}
            />
          ))}
        </SortableContext>
        {tasks.length === 0 && <div className="column-empty muted">No tasks</div>}
        {collapsible && (
          <button className="btn btn-ghost btn-sm btn-block column-more" onClick={() => setExpanded((e) => !e)}>
            {expanded ? "Show less" : `Show ${hidden} more`}
          </button>
        )}
      </div>
      {footer}
    </section>
  );
}
